/**
 * Wave 3 · 23 #8 — "Demand outlook": need vs have per part, week by week, from the real demand (open site
 * requests + BOQ still to supply on Active projects at their installation / handover dates) against stock and
 * LPO quantities still to come — and the week each part runs out, for which project, and when to order.
 * Before the site asks, Forecast says "BE-GT20W.02 runs out the week of 12 Oct for LIV Waterside" — and what to do
 * about it: order the rest, send the draft LPO that already holds it, or expedite the LPO that arrives later
 * (a part that is already ordered or drafted is never "Order now").
 */
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { CalendarClock, CloudOff, FilePlus, Info, Route, TriangleAlert, Truck } from 'lucide-react';
import { formatNumber } from '@/lib/format';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { EmptyState } from '@/components/ui/empty-state';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { CardFoot, CardHead, LinkButton, Segmented, SfCard, TableScroll, plural, tableClass, tdClass, thClass, useIsPhone } from './SfUi';
import { DEMAND_VIEWS, adviceCounts, draftHref, filterDemandParts, outlookHeadline, runAdvice, weekLabel, weekTitle, weekTone } from './demandModel';

const n = (value) => formatNumber(value, { maxDecimals: 2 });
const CELL_TONE = {
  red: 'bg-status-red-bg font-bold text-status-red-ink',
  amber: 'bg-status-amber-bg font-semibold text-status-amber-ink',
  '': 'text-ink-2',
};

function WeekCell({ part, index, weeks }) {
  const need = Number(part.need[index]) || 0;
  const incoming = Number(part.incoming[index]) || 0;
  const balance = Number(part.balance[index]) || 0;
  const tone = weekTone(balance, need);
  const title = weekTitle(part, index, weeks);
  // A week where nothing changes repeats the balance: a quiet bar instead of the same number again.
  const unchanged = index > 0 && !need && !incoming && Number(part.balance[index - 1]) === balance;
  if (unchanged) {
    return (
      <td className={cn(tdClass, 'px-0.5 text-center sm:first:pl-0.5 sm:last:pr-0.5')}>
        <span title={title} aria-label={title} className="mx-auto flex h-8 w-[48px] items-center" data-testid={`demand-cell-${part.part_number}-${index}`}>
          <span className={cn('block h-1.5 w-full rounded-full', tone === 'red' ? 'bg-status-red-bg' : tone === 'amber' ? 'bg-status-amber-bg' : 'bg-[color:var(--line-2)]')} />
        </span>
      </td>
    );
  }
  return (
    <td className={cn(tdClass, 'px-0.5 text-center sm:first:pl-0.5 sm:last:pr-0.5')}>
      <span
        title={title}
        aria-label={title}
        className={cn('relative mx-auto flex h-8 w-[48px] flex-col items-center justify-center rounded-md text-[12px] tabular-nums leading-none', CELL_TONE[tone], !need && !incoming && !tone && 'text-ink-4')}
        data-testid={`demand-cell-${part.part_number}-${index}`}
      >
        {need || incoming || tone ? n(balance) : '·'}
        {need ? <span className="mt-0.5 text-[9.5px] font-medium opacity-80">−{n(need)}</span> : incoming ? <span className="mt-0.5 text-[9.5px] font-medium text-status-green-ink">+{n(incoming)}</span> : null}
      </span>
    </td>
  );
}

function RunsOut({ part }) {
  const run = part.run_out;
  if (!run) {
    return <span className="text-[12px] text-ink-3">Covered{part.beyond_need ? ` · ${n(part.beyond_need)} later` : ''}</span>;
  }
  return (
    <span className="block leading-tight">
      <span className="block whitespace-nowrap text-[13px] font-semibold text-status-red-ink">wk of {weekLabel(run.week_start)}</span>
      <span className="block truncate text-[12px] text-ink-3" title={run.project_name}>{run.project_name || 'Project'} · {n(run.short_qty)} short</span>
    </span>
  );
}

const STEP_TONE = { red: 'font-semibold text-status-red-ink', amber: 'font-semibold text-status-amber-ink', navy: 'font-semibold text-status-navy-ink', '': 'text-ink' };

/** The next step for a part that runs out: Order by / Now · Send draft (link) · Expedite (the LPO that arrives later). */
function NextStep({ part }) {
  const advice = runAdvice(part.run_out, part.unit);
  if (!advice) return <span className="text-ink-4" aria-label="Nothing to order">—</span>;
  const lead = `${part.lead_time_days} d lead${part.lead_time_is_default ? ' · default' : ''}`;
  const sub = advice.action === 'send_draft' ? (
    <Link to={draftHref(advice.draft.lpo_number)} className="block truncate text-[11.5px] text-brand-ink hover:underline" title={advice.sentence} data-testid={`demand-draft-${part.part_number}`}>
      <span className="font-mono">{advice.draft.lpo_number}</span>{advice.draft.status ? ` · ${advice.draft.status}` : ''}
    </Link>
  ) : advice.action === 'expedite' && advice.cover ? (
    <span className="block truncate text-[11.5px] text-ink-3" title={advice.sentence}>
      <span className="font-mono">{advice.cover.lpo_number}</span> · {weekLabel(advice.cover.expected)}
    </span>
  ) : (
    <span className="block truncate text-[11.5px] text-ink-3" title={advice.sentence}>{advice.sub || lead}</span>
  );
  return (
    <span className="block min-w-0 leading-tight" data-testid={`demand-next-${part.part_number}`} data-action={advice.action}>
      <span className={cn('flex items-center gap-1 whitespace-nowrap text-[13px]', STEP_TONE[advice.tone])}>
        {advice.action === 'expedite' ? <Truck className="h-3.5 w-3.5 shrink-0" aria-hidden="true" /> : advice.action === 'send_draft' ? <FilePlus className="h-3.5 w-3.5 shrink-0" aria-hidden="true" /> : null}
        {advice.step}
      </span>
      {sub}
    </span>
  );
}

/**
 * props: outlook (GET /forecast/demand), loading, error, onRetry, onTrace({ part, description }) (optional),
 * search (the page search narrows the rows).
 */
export function DemandOutlookCard({ outlook, loading, error, onRetry, onTrace, search = '' }) {
  const isPhone = useIsPhone();
  const [view, setView] = useState('short');
  const weeks = outlook?.weeks || [];
  const summary = outlook?.summary || {};
  const parts = useMemo(() => filterDemandParts(outlook?.parts, { view, search }), [outlook, view, search]);
  const headline = outlookHeadline(outlook);
  const counts = { short: summary.running_out || 0, all: summary.parts_with_need || 0 };
  const advised = adviceCounts(summary);
  const headTone = !headline?.advice || headline.advice.tone === 'red' || headline.advice.action === 'order' ? 'red' : 'amber';

  if (error) {
    return (
      <SfCard data-testid="demand-outlook">
        <CardHead icon={CalendarClock} title="Demand outlook" />
        <EmptyState layout="inline" tone="red" icon={CloudOff} title="Couldn't load the demand outlook" text={error}
          action={onRetry ? <Button variant="outline" size="sm" onClick={onRetry}>Try again</Button> : null} />
      </SfCard>
    );
  }
  if (!outlook) {
    return loading ? <div className="ui-skeleton-shimmer h-[220px] rounded-card" aria-busy="true" data-testid="demand-outlook-loading" /> : null;
  }

  const unscheduled = summary.unscheduled_pcs > 0 ? (
    <span className="inline-flex flex-wrap items-center gap-x-1.5">
      <Info className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
      {n(summary.unscheduled_pcs)} pcs of BOQ on {plural(summary.unscheduled_projects, 'project')} have no planned dates — not placed in a week.
      <Popover>
        <PopoverTrigger asChild><LinkButton data-testid="demand-unscheduled">Which projects?</LinkButton></PopoverTrigger>
        <PopoverContent align="start" className="w-[300px] rounded-xl border-line bg-surface p-3.5 text-[12.5px] text-ink shadow-overlay">
          <p className="font-heading text-[13.5px] font-bold">Projects without dates</p>
          <p className="mt-0.5 text-[12px] text-ink-3">Plan them in the project’s Timeline (Auto-plan) or set a handover date, and their BOQ joins the weeks.</p>
          <ul className="mt-2 space-y-0.5">{(outlook.unscheduled_project_names || []).map((name, index) => <li key={`${name}-${index}`}>{name}</li>)}</ul>
          {summary.unscheduled_projects > (outlook.unscheduled_project_names || []).length ? <p className="mt-1 text-ink-3">+ {summary.unscheduled_projects - outlook.unscheduled_project_names.length} more</p> : null}
        </PopoverContent>
      </Popover>
    </span>
  ) : null;

  return (
    <SfCard data-testid="demand-outlook" className={loading ? 'opacity-70 transition-opacity' : undefined}>
      <CardHead
        icon={CalendarClock}
        iconTone="navy"
        title="Demand outlook"
        sub={`Next ${weeks.length} weeks · site requests + BOQ still to supply${advised ? ` · ${advised}` : ''}`}
        right={(
          <Segmented
            size="sm"
            ariaLabel="Which parts"
            value={view}
            onChange={setView}
            testId="demand-view"
            options={DEMAND_VIEWS.map((option) => ({ key: option.key, label: option.label, count: counts[option.key], dot: option.key === 'short' && counts.short ? 'red' : undefined, testId: `demand-view-${option.key}` }))}
          />
        )}
      />
      {headline ? (
        <div className={cn('flex flex-wrap items-start gap-x-2 gap-y-1 border-b border-line-2 px-4 py-2.5 text-[13px] text-ink sm:px-[18px]', headTone === 'red' ? 'bg-status-red-bg' : 'bg-status-amber-bg')} role="note" data-testid="demand-headline">
          {headline.advice?.action === 'expedite'
            ? <Truck className="mt-0.5 h-4 w-4 shrink-0 text-status-amber-ink" aria-hidden="true" />
            : <TriangleAlert className={cn('mt-0.5 h-4 w-4 shrink-0', headTone === 'red' ? 'text-status-red-ink' : 'text-status-amber-ink')} aria-hidden="true" />}
          <span className="min-w-0 flex-1">
            <b className="font-mono font-semibold">{headline.part}</b>{headline.description ? <span className="text-ink-2"> {headline.description}</span> : null} runs out the <b>week of {headline.week}</b>
            {headline.project ? <> for <b>{headline.project}</b></> : null} — {n(headline.short)} {headline.unit} short.{' '}
            {headline.advice ? (
              <span className={cn(headline.advice.tone === 'red' ? 'font-semibold text-status-red-ink' : headline.advice.tone === 'amber' ? 'font-semibold text-status-amber-ink' : 'text-ink-2')}>
                {headline.advice.sentence}
                {headline.advice.action === 'send_draft' && headline.advice.draft?.lpo_number ? <> <Link to={draftHref(headline.advice.draft.lpo_number)} className="font-semibold text-brand-ink hover:underline" data-testid="demand-headline-draft">Open draft</Link></> : null}
              </span>
            ) : null}
          </span>
          {summary.running_out > 1 ? <span className="text-[12.5px] text-ink-3">+ {plural(summary.running_out - 1, 'more part')}</span> : null}
        </div>
      ) : null}
      {!parts.length ? (
        <EmptyState
          icon={CalendarClock}
          title={view === 'short' ? `Nothing runs out in the next ${weeks.length} weeks` : `Nothing needed in the next ${weeks.length} weeks`}
          text={view === 'short' && counts.all ? `${plural(counts.all, 'part')} are needed and covered by stock or deliveries on time.` : 'Site requests and planned BOQ show up here once they fall in the window.'}
          action={view === 'short' && counts.all ? <Button variant="outline" size="sm" onClick={() => setView('all')}>Show all needed</Button> : null}
          data-testid="demand-empty"
        />
      ) : isPhone ? (
        <ul className="divide-y divide-line-2" data-testid="demand-rows">
          {parts.map((part) => (
            <li key={part.part_number} className="px-4 py-2.5" data-testid={`demand-row-${part.part_number}`}>
              <div className="flex items-baseline justify-between gap-2">
                <span className="font-mono text-[13px] font-semibold text-ink">{part.part_number}</span>
                <RunsOut part={part} />
              </div>
              <p className="truncate text-[12px] text-ink-3">{part.description || '—'} · have {n(part.have_now)} · need {n(part.need_in_window)}</p>
              {part.run_out ? <div className="mt-1"><NextStep part={part} /></div> : null}
            </li>
          ))}
        </ul>
      ) : (
        <TableScroll>
          <table className={cn(tableClass, 'min-w-[980px] table-fixed')} data-testid="demand-rows">
            <colgroup>
              <col style={{ width: 230 }} /><col style={{ width: 70 }} />
              {weeks.map((week) => <col key={week.start} style={{ width: 54 }} />)}
              <col style={{ width: 160 }} /><col style={{ width: 172 }} />
            </colgroup>
            <thead>
              <tr>
                <th className={thClass}>Part</th>
                <th className={cn(thClass, 'text-right')} title="Stock on hand minus stock booked for projects">Have</th>
                {weeks.map((week, index) => (
                  <th key={week.start} className={cn(thClass, 'px-0.5 text-center normal-case tracking-normal sm:first:pl-0.5 sm:last:pr-0.5')} title={`Week of ${weekLabel(week.start)}`}>
                    {index === 0 ? 'This wk' : weekLabel(week.start)}
                  </th>
                ))}
                <th className={thClass}>Runs out</th>
                <th className={thClass} title="Order by · send the draft that holds it · expedite the LPO that arrives later">Next step</th>
              </tr>
            </thead>
            <tbody>
              {parts.map((part) => (
                <tr key={part.part_number} className="hover:bg-surface-hover" data-testid={`demand-row-${part.part_number}`}>
                  <td className={tdClass}>
                    {onTrace ? (
                      <button type="button" onClick={() => onTrace({ part: part.part_number, description: part.description })} title="Where is it? — requests, orders, deliveries, stock"
                        className="inline-flex items-center gap-1 rounded font-mono text-[12.5px] font-semibold text-ink hover:text-brand-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" data-testid={`demand-trace-${part.part_number}`}>
                        {part.part_number}<Route className="h-3 w-3 text-ink-4" aria-hidden="true" />
                      </button>
                    ) : <span className="font-mono text-[12.5px] font-semibold text-ink">{part.part_number}</span>}
                    <span className="block truncate text-[12px] text-ink-3" title={part.description}>{part.description || '—'}</span>
                    {part.late_incoming ? <span className="block truncate text-[11.5px] text-status-amber-ink" title={`On ${part.late_lpos.join(', ')} — past its expected date, not counted`}>+{n(part.late_incoming)} late on {part.late_lpos[0]}{part.late_lpos.length > 1 ? ` +${part.late_lpos.length - 1}` : ''} · not counted</span> : null}
                  </td>
                  <td className={cn(tdClass, 'text-right font-semibold tabular-nums')} title={`${n(part.on_hand)} on hand${part.booked ? ` − ${n(part.booked)} booked` : ''}`}>{n(part.have_now)}</td>
                  {weeks.map((week, index) => <WeekCell key={week.start} part={part} index={index} weeks={weeks} />)}
                  <td className={tdClass}><RunsOut part={part} /></td>
                  <td className={tdClass}><NextStep part={part} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableScroll>
      )}
      <CardFoot className="text-[12px]">
        <span className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <span>Each week: what is left at its end (<span className="font-semibold text-status-red-ink">red</span> = short), −needed / +arriving; a bar = no change. A part already on an LPO or a draft is never “order now”.</span>
        </span>
        {unscheduled}
      </CardFoot>
    </SfCard>
  );
}
